import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Trophy } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const axis = { stroke: "#444", fontSize: 10, fontFamily: "monospace" };
const grid = "#181818";

function ChartCard({ title, children, testid }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-4" data-testid={testid}>
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mb-3">{title}</div>
      <div style={{ width: "100%", height: 200 }}>{children}</div>
    </div>
  );
}

export default function Progress() {
  const [pbs, setPbs] = useState([]);
  const [prog, setProg] = useState(null);

  useEffect(() => {
    api.get("/personal-bests").then(r => setPbs(r.data.bests || [])).catch(() => {});
    api.get("/progress").then(r => setProg(r.data)).catch(() => {});
  }, []);

  const compliance = prog?.compliance || [];

  return (
    <AppShell>
      <div data-testid="progress-page">
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">LONG-TERM PROGRESS</div>
        <h1 className="font-display text-3xl sm:text-5xl uppercase">Your <span className="text-[#d7ff00]">progress</span></h1>

        {/* Personal Bests */}
        <div className="mt-6 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#d7ff00]" />
          <span className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Personal Bests</span>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pb-grid">
          {pbs.map(b => (
            <div key={b.key} className="bg-[#0a0a0a] border border-[#1f1f1f] p-3" data-testid={`pb-${b.key}`}>
              <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666]">{b.label}</div>
              <div className="font-display text-2xl text-white mt-1">{b.value || "—"}</div>
              <div className="font-mono-jp text-[8px] uppercase tracking-widest text-[#555] mt-1">
                {b.value ? (b.source === "manual" ? "Coach set" : (b.achieved_at || "auto")) : "Not set yet"}
              </div>
            </div>
          ))}
        </div>

        {/* Program comparison */}
        <div className="mt-8 font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Program comparison</div>
        <div className="mt-3 bg-[#0a0a0a] border border-[#1f1f1f] overflow-x-auto" data-testid="comparison-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666] text-left">
                <th className="px-3 py-2">Program</th>
                <th className="px-3 py-2">Compliance</th>
                <th className="px-3 py-2">Distance</th>
                <th className="px-3 py-2">Avg RPE</th>
              </tr>
            </thead>
            <tbody>
              {compliance.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-[#888]">No program data yet.</td></tr>}
              {compliance.map((c, i) => (
                <tr key={i} className="border-t border-[#161616]">
                  <td className="px-3 py-2 text-white">{c.title} {c.status === "current" && <span className="text-[#d7ff00] text-[10px]">· current</span>}</td>
                  <td className="px-3 py-2 text-[#d7ff00]">{c.compliance}%</td>
                  <td className="px-3 py-2 text-[#ddd]">{c.distance} km</td>
                  <td className="px-3 py-2 text-[#ddd]">{c.avg_rpe ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Graphs */}
        <div className="mt-8 grid gap-3 md:grid-cols-2">
          <ChartCard title="Weekly mileage" testid="chart-weekly-mileage">
            <ResponsiveContainer>
              <BarChart data={prog?.weekly_mileage || []}>
                <CartesianGrid stroke={grid} vertical={false} />
                <XAxis dataKey="week" tick={axis} hide />
                <YAxis tick={axis} width={28} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }} />
                <Bar dataKey="km" fill="#d7ff00" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Average pace trend (sec/km, lower = faster)" testid="chart-pace">
            <ResponsiveContainer>
              <LineChart data={prog?.pace_trend || []}>
                <CartesianGrid stroke={grid} vertical={false} />
                <XAxis dataKey="month" tick={axis} />
                <YAxis tick={axis} width={34} domain={["dataMin - 15", "dataMax + 15"]} reversed />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }} formatter={(v, n, p) => [p?.payload?.pace + " /km", "pace"]} />
                <Line type="monotone" dataKey="pace_sec" stroke="#71c4ff" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Recovery / readiness trend" testid="chart-recovery">
            <ResponsiveContainer>
              <LineChart data={prog?.recovery_trend || []}>
                <CartesianGrid stroke={grid} vertical={false} />
                <XAxis dataKey="date" tick={axis} hide />
                <YAxis tick={axis} width={28} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }} />
                <Line type="monotone" dataKey="score" stroke="#7fffd4" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Compliance by program (%)" testid="chart-compliance">
            <ResponsiveContainer>
              <BarChart data={compliance}>
                <CartesianGrid stroke={grid} vertical={false} />
                <XAxis dataKey="title" tick={axis} hide />
                <YAxis tick={axis} width={28} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }} />
                <Bar dataKey="compliance" fill="#c084fc" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </AppShell>
  );
}
